import { Currency } from "@models/interfaces/currency";
import { Pipe, PipeTransform } from "@angular/core";
import { Fiat } from "@models";
import { FiatService } from "@main-services/currency/fiat.service";
import { PrecisionInterface } from "./currency.precision.pipe";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { Service } from "@main-services/base-service";
import { Tradable } from "@models/interfaces/tradable";
import { Metal } from "@models/asset/metal/metal";
import { TranslationService } from "@core-services/translation.service";

export abstract class CurrencySymbolPipe<C extends Currency> {
	protected abstract tradableListService: Service<C>;

	public constructor(protected translationService: TranslationService) {}

	protected getCurrency(id: string): C {
		return this.tradableListService.getItemById(id);
	}

	public transform(args: PrecisionInterface): string {
		const currency = this.getCurrency(args.id);
		if (!currency || args.removeCurrency) {
			return "";
		}
		let currencySymbol: string = currency.getSymbol(args.lowercase);
		if (this.isCommodity(args, currency)) {
			currencySymbol = currency.getSymbol() + (args.addName ? " " + currency.getName() : "");
		}

		if (args.showNameInsteadOfSymbol) {
			currencySymbol = currency.getName();
		}

		if (args.showGrams && currencySymbol === "g") {
			const grams = this.translationService.translate("grams");
			currencySymbol = grams.charAt(0).toUpperCase() + grams.slice(1);
		}

		return currencySymbol;
	}

	public isCommodity(args: PrecisionInterface, currency: C = this.getCurrency(args.id)): boolean {
		return currency instanceof Metal;
	}
}

@Pipe({
	name: "tradable_symbol",
	standalone: false
})
export class TradableSymbolPipe extends CurrencySymbolPipe<Tradable> implements PipeTransform {
	public constructor(
		protected tradableListService: TradableListService,
		protected translationService: TranslationService
	) {
		super(translationService);
	}

	protected getItemByPid(pid: string): Tradable {
		return this.tradableListService.getItemByPid(pid);
	}

	public transform(args: PrecisionInterface): string {
		let currency: Tradable;
		if (args?.assetPid) {
			currency = this.getItemByPid(args.assetPid);
		} else {
			currency = this.getCurrency(args.id);
		}

		if (!currency || args.removeCurrency) {
			return "";
		}
		let currencySymbol: string = currency.getSymbol(args.lowercase);
		if (this.isCommodity(args, currency)) {
			currencySymbol = currency.getSymbol() + (args.addName ? " " + currency.getName() : "");
		}

		if (args.showNameInsteadOfSymbol) {
			currencySymbol = currency.getName();
		}

		if (args.showGrams && currencySymbol === "g") {
			const grams = this.translationService.translate("grams");
			currencySymbol = grams.charAt(0).toUpperCase() + grams.slice(1);
		}

		return currencySymbol;
	}
}

@Pipe({
	name: "fiat_symbol",
	standalone: false
})
export class FiatSymbolPipe extends CurrencySymbolPipe<Fiat> implements PipeTransform {
	public constructor(
		protected tradableListService: FiatService,
		protected translationService: TranslationService
	) {
		super(translationService);
	}

	public transform(args: PrecisionInterface): string {
		if (args.symbol && !args.removeCurrency && !!this.getCurrency(args.id)) {
			return this.getCurrency(args.id).getSymbolCharacter();
		}
		return super.transform(args);
	}
}

@Pipe({
	name: "access_tradable_symbol",
	standalone: false
})
export class AccessTradableSymbolPipe implements PipeTransform {
	transform(tradable: Tradable): string {
		return tradable.getSymbol();
	}
}

@Pipe({
	name: "access_fiat_symbol",
	standalone: false
})
export class AccessFiatSymbolPipe implements PipeTransform {
	transform(fiat: Fiat): string {
		return fiat.getSymbol();
	}
}
